import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CartSheet from '@/components/CartSheet';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (sectionId) => {
    setIsMobileMenuOpen(false);
    if (isHomePage) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 50);
    } else {
      navigate('/');
      const scrollToSection = () => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          setTimeout(scrollToSection, 100);
        }
      };
      setTimeout(scrollToSection, 200);
    }
  };

  const menuItems = [
    { label: 'Početna', sectionId: 'hero' },
   { label: 'O nama', sectionId: 'about' },
    { label: 'Proizvodi', sectionId: 'products' },
    { label: 'Kontakt', sectionId: 'contact' }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background/98 backdrop-blur-md shadow-md border-b border-border'
          : 'bg-background/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button 
              onClick={() => handleNavigation('hero')}
              className="flex items-center gap-3 group cursor-pointer touch-manipulation"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_serb-landing/artifacts/fwicggqw_logo" 
                alt="Nameštaj Sanin Logo"
                className="h-10 w-10 lg:h-12 lg:w-12 transition-transform duration-300 group-hover:scale-105"
              />
              <div className="flex items-baseline">
                <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary tracking-tight transition-colors">
                  Nameštaj
                </span>
                <span className="ml-2 text-xl sm:text-2xl lg:text-3xl font-light text-muted-foreground">Sanin</span>
              </div>
            </button>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavigation(item.sectionId)}
                className="text-sm lg:text-base font-medium text-foreground/70 hover:text-primary transition-colors duration-200 relative group cursor-pointer touch-manipulation"
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </div>

          {/* Korpa + CTA */}
          <div className="hidden md:flex items-center gap-2">
            <CartSheet />
            <Button
              size="default"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium transition-all duration-300 shadow-md hover:shadow-lg lg:px-6 lg:py-5 lg:text-base touch-manipulation cursor-pointer"
              onClick={() => handleNavigation('contact')}
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            >
              Kontaktirajte nas
            </Button>
          </div>

          <div className="flex md:hidden items-center gap-1">
            <CartSheet />
            <button
            className="p-2 rounded-lg hover:bg-muted transition-colors touch-manipulation cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-foreground" />
            )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-card border-t border-border shadow-lg">
          <div className="container mx-auto px-4 py-6 space-y-4">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavigation(item.sectionId)}
                className="w-full text-left block py-3 text-base font-medium text-foreground/70 hover:text-primary transition-colors border-b border-border/30 last:border-0 cursor-pointer touch-manipulation"
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              >
                {item.label}
              </button>
            ))}
            <div className="pt-4">
              <Button
                size="lg"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-md touch-manipulation cursor-pointer"
                onClick={() => handleNavigation('contact')}
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              >
                Kontaktirajte nas
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
